import { useMemo, useRef, useState } from "react";
import { capitalizeWords } from "@rebuild/shared";
import { cn } from "@/lib/utils";
import { AgentPanel } from "./agent/AgentPanel";
import { useFeatureFlags } from "@/context/FeatureFlagsContext";
import { EstimateType } from "@/enums/estimate-type";
import { DOCUMENT_FILE_TYPES, EstimateFile } from "@/enums/estimate-file";
import { estimationActions } from "@/redux/actions/estimation.actions";
import { useDispatch } from "@/redux/store";
import { RightSidebarHeader } from "./RightSidebarHeader";
import { useDetailsSectionData } from "./useDetailsSectionData";
import { useTPARules } from "./useTPARules";
import { DetailsSectionContent } from "./DetailsSectionContent";
import { DetailsSectionTabs, type TabOption } from "./DetailsSectionTabs";
import TPARulesConfirmationModal from "../TPARulesConfirmationModal";
import { useGetEstimateResponsiveNotePhotosQuery } from "@/hooks/queries/responsive-note-photos";
import { normalizeEstimateFilePhoto } from "./Photos/photos.utils";

interface DetailsSectionProps {
  estimateId?: string;
}

const DetailsSection = ({ estimateId }: DetailsSectionProps) => {
  const dispatch = useDispatch();
  const photoPreviewOverlayRef = useRef<HTMLDivElement>(null);

  const { rightSidebarCurrentTab, currentRoom, lineItemDetail, unSelectLineItem, estimation, files, links } =
    useDetailsSectionData(estimateId!);
  const { data: responsiveNotePhotos = [] } = useGetEstimateResponsiveNotePhotosQuery(estimateId);

  const tabCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    counts["Notes"] = estimation?.scopeNoteObjects?.length ?? 0;
    counts["Photos"] =
      files.filter((f) => f.fileType === EstimateFile.IMAGE && normalizeEstimateFilePhoto(f) !== null).length + responsiveNotePhotos.length;
    counts["Documents"] =
      estimation?.pdfPagesAsImages?.length ||
      files.filter((f) => DOCUMENT_FILE_TYPES.includes(f.fileType as EstimateFile)).length;
    counts["Links"] = links.length;
    return counts;
  }, [estimation, files, links, responsiveNotePhotos.length]);

  const { isTPARulesModalOpen, phase, selectedTPAFields, updateSelectedTPAField, closeTPARulesModal, submitNewTPAFields } =
    useTPARules(estimateId!);

  const onSelectTab = (option: TabOption) => {
    dispatch(
      estimationActions.setEstimationState({
        rightSidebarCurrentTab: { id: option.id, value: option.value },
      }),
    );
  };

  const roomName = currentRoom?.value ?? "";
  const selectedTab = rightSidebarCurrentTab?.value;

  const { isFeatureEnabled } = useFeatureFlags();
  // The agent doesn't support T&M estimates: they price from a rate card it can't search.
  const isAgentEnabled = isFeatureEnabled("VIBE_ESTIMATING_ENABLED") && estimation?.estimateType !== EstimateType.TIME_AND_MATERIAL;
  // only mount agent panel once the Agent tab has actually been opened.
  const [agentOpened, setAgentOpened] = useState(false);
  if (isAgentEnabled && selectedTab === "Agent" && !agentOpened) setAgentOpened(true);

  return (
    <>
      <div className="relative flex h-full flex-auto flex-col border-b border-[#eeeff1] transition-all duration-300">
        <div className={`flex items-center border-[#eeeff1]`}>
          {selectedTab !== "Item" && (
            <DetailsSectionTabs
              selectedId={rightSidebarCurrentTab?.id}
              onSelect={onSelectTab}
              counts={tabCounts}
              isAgentEnabled={isAgentEnabled}
            />
          )}
          {selectedTab === "Item" && (
            <RightSidebarHeader
              onBack={unSelectLineItem}
              title={roomName}
              subTitle={capitalizeWords(lineItemDetail?.lineItem.lineItem || "")}
              divider="/"
            />
          )}
        </div>

        <div
          className={cn(
            "h-full flex-1 border-[#eeeff1] transition-all duration-300",
            // The chat panel owns its own scrolling (message list scrolls, composer pinned),
            // so the sidebar's always-on scrollbar would paint a dead second track.
            selectedTab === "Agent" ? "overflow-hidden" : "overflow-y-scroll",
          )}
        >
          {/*
            Kept mounted rather than rendered through the switch below. Selecting a line
            item swaps the sidebar to the "Item" pseudo-tab, which would otherwise unmount
            this and take scroll position, the composer draft, and any in-flight stream
            with it — so returning from a line item wouldn't land "where you left it".
          */}
          {agentOpened && (
            <div className={cn("h-full", selectedTab !== "Agent" && "hidden")}>
              <AgentPanel estimateId={estimateId!} />
            </div>
          )}

          <DetailsSectionContent
            selectedTab={selectedTab}
            estimateId={estimateId!}
            selectedTPAFields={selectedTPAFields}
            updateSelectedTPAField={updateSelectedTPAField}
            photoPreviewOverlayRef={photoPreviewOverlayRef}
          />
        </div>
        <div ref={photoPreviewOverlayRef} className="pointer-events-none absolute inset-0 z-50" />
      </div>

      <TPARulesConfirmationModal
        open={isTPARulesModalOpen}
        phase={phase}
        onClose={() => {
          closeTPARulesModal();
        }}
        onConfirm={submitNewTPAFields}
        selectedTPAFields={selectedTPAFields}
        updateSelectedTPAField={updateSelectedTPAField}
      />
    </>
  );
};

export default DetailsSection;
